import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  TagProps,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  CheckCircleFilled,
  ExportOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { CampaignDetails, CampaignInfo } from "../models/campaign";
import { useBrandStore } from "../store/brandStore";
import { campaignService } from "../services/campaignService";
import {
  CampaignTypeEnum,
  ChannelEnum,
  ModeEnum,
  StatusEnum,
  TemplatesTypeEnum,
} from "../constants/enums";
import {
  CampaignTypeLabel,
  ChannelKeyMap,
  getTemplateLabel,
  rfmPeriodToLabel,
  StatusKeyMap,
} from "../constants/labels";
import DOMPurify from "dompurify";
import { RoutePaths } from "../routes/route";
import { useQueryNavigator } from "../utils/navigate";
import CampaignAlertModal from "../components/common/CampaignAlertModal";
import { Variant } from "../constants/type";
import { Image as AntdImage } from "antd";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CardData } from "../models/campaignPerformances";
import CampaignMemberLogTable from "../components/common/CampaignMemberLogTable";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import CustomModal from "../components/common/Modal";
import BillingModal from "../components/common/BillingModal";
import { mapToEVouchers, whatsappTextToHtml } from "../utils/convert";
import { getChannelCost } from "../constants/cost";
import { getSmsCharCount, getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import PreviewMemberModal from "../components/common/PreviewMemberModal";

const currencySGD = (v: number) => `SGD ${v.toFixed(2)}`;

type Row = { key: string; field: React.ReactNode; value: React.ReactNode };

const columns: ColumnsType<Row> = [
  { title: null, dataIndex: "field", key: "field", width: "25%" },
  { title: null, dataIndex: "value", key: "value" },
];

const DetailCampaign: React.FC = () => {
  const initialCardData: CardData[] = [
    {
      key: "card-data-1",
      title: "Revenue via Rewards",
      value: "$0",
      icon: "/icon/icon-sack-dollar.svg",
      order: 1,
      tooltip: (
        <>
          Total revenue generated from customers who received and
          <br />
          responded to this campaign (generated with voucher or point usage)
        </>
      ),
    },
    {
      key: "card-data-2",
      title: "Revenue from Campaign",
      value: "$0",
      order: 2,

      icon: "/icon/icon-revenue-campaign.svg",
      tooltip: (
        <>
          Total revenue generated from campaign activities based on targeted
          members spending
        </>
      ),
    },
    {
      key: "card-data-3",
      title: "Return of Investment (ROI)",
      value: "0%",
      icon: "/icon/icon-roi.svg",
      tooltip: (
        <>
          Return on Investment = (Revenue – Cost) / Cost.
          <br />
          Based on the total campaign cost and revenue.
        </>
      ),
    },
    {
      key: "card-data-4",
      title: "Click Through Rate (CTR)",
      value: "0%",
      icon: "/icon/icon-ctr.svg",
      tooltip: (
        <>
          Click-through rate = (Number of clicks / Messages delivered)
          <br />× 100%
        </>
      ),
    },
  ];
  const [cardData, setCardData] = useState<CardData[]>(initialCardData);
  const [loading, setLoading] = useState<boolean>(false);
  const [messageTimeoutModal, setMessageTimeoutModal] = useState(
    "This campaign is approaching its scheduled send time, so editing is no longer available",
  );
  const isMobile = window.innerWidth < 768;

  const [titleTimeoutModal, setTitleTimeoutModal] = useState(
    "Editing Unavailable",
  );

  const navigate = useNavigate();
  const [data, setData] = useState<CampaignDetails | null>(null);
  const { brandId } = useBrandStore();
  const { search } = useLocation();
  const query = new URLSearchParams(search);
  const id = query.get("id");
  const html = data?.Messages?.[0]?.Content ?? "";
  const safeHtml = DOMPurify.sanitize(html);
  const location = useLocation();
  const navigateWithQuery = useQueryNavigator();
  const [variant, setVariant] = useState<Variant | null>(null);
  const { Title, Text } = Typography;
  const [actionType, setActionType] = useState<string | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);
  const [loadingEVoucher, setLoadingEVoucher] = useState(false);
  const [campaignInfo, setCampaignInfo] = useState<CampaignInfo | null>(null);

  const [selectedEVoucher, setSelectedEVoucher] = useState<EVoucher | null>(
    null,
  );
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);

  const [
    isModalSuccessDeleteCampaignVisible,
    setIsModalSuccessDeleteCampaignVisible,
  ] = useState(false);
  const [
    isModalSuccessActivateCampaignVisible,
    setIsModalSuccessActivateCampaignVisible,
  ] = useState(false);
  const [price, setPrice] = useState(0);

  // Billing flow
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingContext, setBillingContext] = useState<"activate" | "pay">(
    "activate",
  );
  const [paymentSuccessModalOpen, setPaymentSuccessModalOpen] = useState(false);
  const [paymentFailedModalOpen, setPaymentFailedModalOpen] = useState(false);

  const STATUS_COLOR: Record<StatusEnum, TagProps["color"]> = {
    [StatusEnum.Draft]: undefined,
    [StatusEnum.Active]: "green",
    [StatusEnum.Paused]: "orange",
    [StatusEnum.Completed]: "blue",
    [StatusEnum.Cancelled]: "red",
  };
  const start = data?.CampaignStart ? new Date(data.CampaignStart) : null;

  // Derive billing status from the latest run log
  const isBilled =
    (data?.CampaignRunLog ?? [])
      .slice()
      .sort(
        (a, b) =>
          new Date(b.CreatedDate ?? 0).getTime() -
          new Date(a.CreatedDate ?? 0).getTime(),
      )[0]?.IsBilled ?? false;

  const isWithinOneHour = (() => {
    if (!start) return false;

    const now = new Date();
    const diffInHours = (start.getTime() - now.getTime()) / (1000 * 60 * 60);

    return diffInHours <= 1;
  })();

  const onClickAction = (variant: Variant) => {
    if (!start) return false;

    const now = new Date();
    const diffInHours = (start.getTime() - now.getTime()) / (1000 * 60 * 60);
    if (
      diffInHours <= 1 &&
      ((data?.Status === StatusEnum.Draft && variant === "activate") ||
        (data?.Status === StatusEnum.Active && variant === "cancel"))
    ) {
      if (variant === "activate") {
        setTitleTimeoutModal("Activation Unavailable");
        setMessageTimeoutModal(
          "The campaign is currently locked due to its upcoming send time. Please Update the campaign details before activating it.",
        );
      }
      if (variant === "cancel") {
        setTitleTimeoutModal("Cancelling Unavailable");
        setMessageTimeoutModal(
          "The campaign is locked as it gets closer to the scheduled send time.",
        );
      }
      setShowTimeout(true);
    } else {
      setVariant(variant);
    }
  };

  const [showTimeout, setShowTimeout] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);
  const isUnicodeText = !isGsmText(data?.Messages[0].Content ?? "");
  const smsSegments = getSmsSegments(
    data?.Messages[0].Content ?? "",
    isUnicodeText,
  );

  const maxReach = useMemo(() => {
    const budgetFinal = data?.Budget ?? 0;
    const cost = getChannelCost(data?.Channel ?? null);
    if (data?.Channel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [smsSegments, data]);

  const statusTag = (s: StatusEnum) => {
    const map: Record<StatusEnum, { color: string; label: string }> = {
      [StatusEnum.Draft]: {
        color: "default",
        label: StatusKeyMap[StatusEnum.Draft],
      },
      [StatusEnum.Active]: {
        color: "green",
        label: StatusKeyMap[StatusEnum.Active],
      },
      [StatusEnum.Paused]: {
        color: "orange",
        label: StatusKeyMap[StatusEnum.Paused],
      },
      [StatusEnum.Completed]: {
        color: "blue",
        label: StatusKeyMap[StatusEnum.Completed],
      },
      [StatusEnum.Cancelled]: {
        color: "red",
        label: StatusKeyMap[StatusEnum.Cancelled],
      },
    };
  };

  const overviewRows: Row[] = [
    {
      key: "created",
      field: "Created At",
      value: dayjs(data?.CreatedDate)
        .add(-7, "hour")
        .format("YYYY-MM-DD HH:mm"),
    },
    { key: "name", field: "Campaign Name", value: data?.Name },
    {
      key: "type",
      field: "Campaign Type",
      value: data?.Type ? CampaignTypeLabel[data?.Type] : "-",
    },
    {
      key: "type",
      field: "Campaign Template Type",
      value: data?.TemplateType ? (
        <div>
          {getTemplateLabel(data.TemplateType)}
          <br />
          <span style={{ fontSize: 12, color: "#888" }}>
            {data.TemplateType === TemplatesTypeEnum.Default
              ? "(This template users a pre-configured RFM segmentation targeting members with no transactions or visit in the last 60 days)"
              : "(Fully customizable RFM segmentation. Select your own period, segments and criteria)"}
          </span>
        </div>
      ) : (
        "-"
      ),
    },
    ...(data?.TemplateType === TemplatesTypeEnum.Default
      ? [
          {
            key: "inactiveDays",
            field: "Inactive Period",
            value: data?.InactiveDays ? data?.InactiveDays + " Days" : "-",
          },
        ]
      : []),
    {
      key: "Payment Status",
      field: "IsBilled",
      value: (
        <Space size={[6, 6]} wrap>
          {isBilled != null ? (
            <Tag color={isBilled ? "green" : "red"}>
              {isBilled ? "Paid" : "Unpaid"}
            </Tag>
          ) : (
            "-"
          )}
        </Space>
      ),
    },
    {
      key: "status",
      field: "Status",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.Status != null ? (
            <Tag color={STATUS_COLOR[data.Status as StatusEnum]}>
              {StatusKeyMap[data.Status as StatusEnum]}
            </Tag>
          ) : (
            "-"
          )}
        </Space>
      ),
    },
    {
      key: "channel",
      field: "Channel",
      value: data?.Channel ? ChannelKeyMap[data?.Channel] : "-",
    },
    {
      key: "period",
      field: "Selected RFM Period",
      value:
        data?.RFMPeriod != null
          ? (rfmPeriodToLabel[data.RFMPeriod] ?? "-")
          : "-",
    },
    {
      key: "segments",
      field: "Target RFM Segment",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.Segments && data.Segments.length > 0
            ? data.Segments.map((s) => s.SegmentName).join(", ")
            : "-"}
        </Space>
      ),
    },
    {
      key: "demographic",
      field: "Target Demographics (age)",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.FromAge != null && data?.ToAge != null
            ? `${data.FromAge} - ${data.ToAge}`
            : "-"}
        </Space>
      ),
    },

    {
      key: "geographic",
      field: "Target Geographic (Postal Sector)",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.PostalDistricts && data.PostalDistricts.length > 0
            ? data.PostalDistricts.map((d) =>
                d.PostalSectors && d.PostalSectors.length > 0 ? (
                  <Tag key={`District${d.DistrictId}`}>
                    {d.PostalSectors.join(", ")}
                  </Tag>
                ) : null,
              )
            : "-"}
        </Space>
      ),
    },

    {
      key: "profile",
      field: "RFM Criteria Profile",
      value: data?.RFMProfileName,
    },
    {
      key: "PurchaseBrand",
      field: "Purchase Brand",
      value: (
        <Space size={[6, 6]} wrap>
          {data?.PurchaseBrandList && data.PurchaseBrandList.length > 0
            ? data.PurchaseBrandList.map((d) => <Tag key={`${d}`}>{d}</Tag>)
            : "All Brands"}
        </Space>
      ),
    },
    {
      key: "reach",
      field: "Estimated Total Members",
      value: (
        <Row>
          <Col span={24}> {data?.EstimatedTotalMembers} Members</Col>
        </Row>
      ),
    },
    {
      key: "cpm",
      field: "Channel Cost per Message",
      value: currencySGD(getChannelCost(data?.Channel ?? null)),
    },
    {
      key: "budget",
      field: " Estimated Budget",
      value: currencySGD(
        getChannelCost(data?.Channel ?? null) *
          (data?.EstimatedTotalMembers ?? 0) *
          (data?.Channel === ChannelEnum.Sms ? smsSegments : 1),
      ),
    },
    {
      key: "budget1",
      field: "Budget Amount Entire Campaign (Allocated)",
      value: currencySGD(data?.Budget ?? 0),
    },
    ...(data?.Status === StatusEnum.Completed
      ? [
          {
            key: "finalBudget",
            field: "Final Budget (Actual Spend)",
            value: currencySGD(
              data?.CampaignRunLog?.[0]?.MemberLogs
                ? data.CampaignRunLog[0].MemberLogs.map(
                    (log) => (log.Fee || 0) * (log.Count || 0),
                  ).reduce((sum, val) => sum + val, 0)
                : 0,
            ),
          },
        ]
      : []),
    {
      key: "reach1",
      field: "Maximum Reach",
      value: (
        // `${maxReach} Members`
        <Row>
          <Col xs={24} md={12} xl={12}>
            {(() => {
              const est = data?.EstimatedTotalMembers ?? 0;
              const max = maxReach;

              if (est === max) {
                return `${max} Members`;
              }

              if (max > est) {
                return `${est} of ${max} Members`;
              }

              return `${max} of ${est} Members based on estimation budget`;
            })()}
          </Col>

          <Col
            xs={24}
            md={12}
            xl={12}
            style={{ textAlign: isMobile ? "left" : "right" }}
          >
            {data?.Status === StatusEnum.Active && (
              <span
                onClick={openPreview}
                style={{
                  color: "#1677ff",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontWeight: 500,
                }}
              >
                Preview Members <ExportOutlined />
              </span>
            )}
          </Col>
        </Row>
      ),
    },
    ...(data?.Status === StatusEnum.Completed
      ? [
          {
            key: "totalMembers",
            field: "Final Reach Members",
            value: (
              <>
                {data?.CampaignRunLog?.[0]?.TotalMembers + " Members"}
                <b>
                  {" (" +
                    data?.CampaignRunLog?.[0]?.SuccessCount +
                    " Success | " +
                    data?.CampaignRunLog?.[0]?.FailCount +
                    " Failed)"}
                </b>
              </>
            ),
          },
        ]
      : []),
  ];

  const scheduleRows: Row[] = [
    {
      key: "stype",
      field: "Schedule Campaign",
      value:
        data?.ScheduleType === 1 ? "One-Time Campaign" : "Recurring Campaign",
    },
    {
      key: "sendat",
      field: "Send Date & Time",
      value: (() => {
        const start = dayjs(data?.CampaignStart).format("YYYY-MM-DD HH:mm");
        if (data?.Status === StatusEnum.Cancelled) {
          return `${start}`;
        }

        if (data?.ScheduleType === 1 && data?.CampaignStart) {
          const end = data.CampaignEnd
            ? dayjs(data.CampaignEnd).format("YYYY-MM-DD HH:mm")
            : null;

          if (data.Status === StatusEnum.Completed) {
            return start;
          }

          return end ? `${start} (Estimated to Complete by ${end})` : start;
        }

        return "-";
      })(),
    },
    {
      key: "mode",
      field: "Target Members Mode",
      value: data?.Mode ? (
        <div>
          {data.Mode === ModeEnum.Refresh
            ? "Refresh target before sending"
            : "Use fixed target"}
          <br />
          <span style={{ fontSize: 12, color: "#888" }}>
            {data.Mode === ModeEnum.Refresh
              ? "(Target members will be refreshed 1 day before send time)."
              : "(Target members remains the same as when the campaign was created)."}
          </span>
        </div>
      ) : (
        "-"
      ),
    },
    ...(data?.Status === StatusEnum.Completed
      ? [
          {
            key: "completedOn",
            field: "Completed On",
            value: data?.CampaignRunLog?.[0]?.EndDate
              ? dayjs(data.CampaignRunLog[0].EndDate)
                  .add(-7, "hour")
                  .format("YYYY-MM-DD HH:mm")
              : "-",
          },
        ]
      : []),
  ];

  const formatContent = (text: string, channel: ChannelEnum) => {
    if (channel === ChannelEnum.WhatsAppStandard) {
      return whatsappTextToHtml(text);
    }

    if (channel === ChannelEnum.WhatsAppPremium) {
      return text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/\\n/g, "\n")
        .replace(/\n\n/g, "<br /><br />")
        .replace(/\n/g, "<br />")
        .replace(/\*(.*?)\*/g, "<strong>$1</strong>");
    }

    return whatsappTextToHtml(text);
  };
  const charCount = getSmsCharCount(data?.Messages[0]?.Content ?? "");
  const previewFieldLabel =
    data?.Channel === ChannelEnum.Sms
      ? `Preview Message (${smsSegments} Message)`
      : "Preview Message";

  const messageRows: Row[] = useMemo(() => {
    return [
      ...(data?.Type === CampaignTypeEnum.Voucher ||
      data?.Type === CampaignTypeEnum.VoucherPoint
        ? [
            {
              key: "reward",
              field: "Reward Included",
              value: `${selectedVoucher?.Name ?? "-"} | ${
                selectedVoucher?.DiscountLabel ?? "-"
              }`,
            },
          ]
        : []),

      ...(data?.Type === CampaignTypeEnum.EVoucher
        ? [
            {
              key: "reward",
              field: "Reward Included",
              value: loadingEVoucher ? (
                <Space>
                  <Spin size="small" />
                  Loading E-Voucher...
                </Space>
              ) : (
                `${selectedEVoucher?.Name ?? "-"} | $${selectedEVoucher?.Price ?? "-"}`
              ),
            },
          ]
        : []),

      ...(data?.Channel === ChannelEnum.WhatsAppPremium
        ? [
            {
              key: "message_template",
              field: "Message Template",
              value: selectedMessageTemplates?.Name ?? "-",
            },
          ]
        : []),

      {
        key: "preview",
        field: previewFieldLabel,
        value: (
          <>
            {data?.ImageUrl && (
              <AntdImage
                src={data.ImageUrl}
                alt="Campaign preview"
                width={250}
                height={150}
                style={{
                  objectFit: "contain",
                  borderRadius: 8,
                  border: "1px solid #f0f0f0",
                }}
                preview={false}
              />
            )}

            <div
              dangerouslySetInnerHTML={{
                __html: data?.Messages?.[0]?.Content
                  ? DOMPurify.sanitize(
                      formatContent(
                        data.Messages[0].Content,
                        data?.Channel ?? ChannelEnum.Sms,
                      ),
                    )
                  : "Your Message will appear here...",
              }}
            />

            {selectedMessageTemplates && (
              <Space
                direction="vertical"
                style={{ width: "100%", marginTop: 16 }}
              >
                {selectedMessageTemplates?.TextLink1 && (
                  <Button
                    block
                    style={{ color: "#00A884", fontWeight: 600 }}
                    icon={
                      <img
                        src="/icon/icon-button-whatsapp.svg"
                        style={{ width: 16 }}
                      />
                    }
                    onClick={() => {
                      const url = data?.Messages?.[0]?.Link;
                      if (url) window.open(url, "_blank");
                    }}
                  >
                    {selectedMessageTemplates.TextLink1}
                  </Button>
                )}

                {selectedMessageTemplates?.TextLink2 && (
                  <Button
                    block
                    style={{ color: "#00A884", fontWeight: 600 }}
                    icon={
                      <img
                        src="/icon/icon-button-whatsapp.svg"
                        style={{ width: 16 }}
                      />
                    }
                  >
                    {selectedMessageTemplates.TextLink2}
                  </Button>
                )}
              </Space>
            )}
          </>
        ),
      },
    ];
  }, [
    data,
    selectedVoucher,
    selectedEVoucher,
    selectedMessageTemplates,
    previewFieldLabel,
    loadingEVoucher,
  ]);

  // const messageRows: Row[] = [
  //   ...(data?.Type === CampaignTypeEnum.Voucher ||
  //   data?.Type === CampaignTypeEnum.VoucherPoint
  //     ? [
  //         {
  //           key: "reward",
  //           field: "Reward Included",
  //           value:
  //             data?.Type === CampaignTypeEnum.Voucher ||
  //             data?.Type === CampaignTypeEnum.VoucherPoint
  //               ? selectedVoucher?.Name + " | " + selectedVoucher?.DiscountLabel
  //               : (data?.SKU ?? "-"),
  //         },
  //       ]
  //     : []),
  //   ...(data?.Type === CampaignTypeEnum.EVoucher
  //     ? [
  //         {
  //           key: "reward",
  //           field: "Reward Included",
  //           value: selectedEVoucher?.Name + " a| $" + selectedEVoucher?.Price,
  //         },
  //       ]
  //     : []),
  //   ...(data?.Channel === ChannelEnum.WhatsAppPremium
  //     ? [
  //         {
  //           key: "message_template",
  //           field: "Message Template",
  //           value: selectedMessageTemplates?.Name,
  //         },
  //       ]
  //     : []),
  //   {
  //     key: "preview",
  //     field: `${previewFieldLabel}`,
  //     value: (
  //       <>
  //         {data?.ImageUrl && (
  //           <AntdImage
  //             src={data?.ImageUrl}
  //             alt="Campaign preview"
  //             width={250}
  //             height={150}
  //             style={{
  //               objectFit: "contain",
  //               borderRadius: 8,
  //               border: "1px solid #f0f0f0",
  //             }}
  //             preview={false}
  //           />
  //         )}
  //         <div
  //           dangerouslySetInnerHTML={{
  //             __html: data?.Messages[0]?.Content
  //               ? DOMPurify.sanitize(
  //                   formatContent(
  //                     data.Messages[0].Content,
  //                     data?.Channel ?? ChannelEnum.Sms,
  //                   ),
  //                 )
  //               : "Your Message will appear here...",
  //           }}
  //         ></div>
  //         {selectedMessageTemplates && (
  //           <Space
  //             direction="vertical"
  //             style={{ width: "100%", marginTop: 16 }}
  //           >
  //             {selectedMessageTemplates?.TextLink1 && (
  //               <Button
  //                 block
  //                 style={{
  //                   color: "#00A884",
  //                   fontWeight: 600,
  //                 }}
  //                 icon={
  //                   <img
  //                     src="/icon/icon-button-whatsapp.svg"
  //                     style={{ width: 16 }}
  //                   />
  //                 }
  //                 onClick={() => {
  //                   const url = data?.Messages?.[0]?.Link;
  //                   if (url) {
  //                     window.open(url, "_blank");
  //                   }
  //                 }}
  //               >
  //                 {selectedMessageTemplates?.TextLink1}
  //               </Button>
  //             )}

  //             {selectedMessageTemplates?.TextLink2 && (
  //               <Button
  //                 block
  //                 style={{
  //                   color: "#00A884",
  //                   fontWeight: 600,
  //                 }}
  //                 icon={
  //                   <img
  //                     src="/icon/icon-button-whatsapp.svg"
  //                     style={{ width: 16 }}
  //                   />
  //                 }
  //               >
  //                 {selectedMessageTemplates?.TextLink2}
  //               </Button>
  //             )}
  //           </Space>
  //         )}
  //         {/* {data?.Messages[0]?.Link} */}
  //       </>
  //     ),
  //   },
  // ];

  const headerStyle: React.CSSProperties = {
    fontWeight: 600,
    background: "#f5f5f5",
    padding: "6px 12px",
    border: "1px solid #f0f0f0",
    borderBottom: 0,
    borderRadius: 4,
  };

  const handleDelete = async () => {
    const result = await campaignService.deleteCampaignDetails(data?.Id ?? 0);
    alert("delete");
  };

  const handleActivate = () => {
    alert("activate");
  };

  const handleEdit = () => {
    navigateWithQuery(RoutePaths.IFRAME_UPDATE_CAMPAIGN, "id", data?.Id);
  };

  const fetchCampaignData = async () => {
    if (id) {
      try {
        setLoading(true);
        const data = await campaignService.getCampaignDetailsById(Number(id));

        if (data) {
          setData(data?.Campaign);

          switch (data?.Campaign?.Channel) {
            case ChannelEnum.Sms:
              setPrice(Number(process.env.REACT_APP_SMS_COST));
              break;

            case ChannelEnum.WhatsAppPremium:
              setPrice(Number(process.env.REACT_APP_WHATSAPP_COST));
              break;

            case ChannelEnum.WhatsAppStandard:
              setPrice(Number(process.env.REACT_APP_WHATSAPP_COST));
              break;

            default:
              setPrice(0);
          }

          if (data?.Voucher) {
            setSelectedVoucher(data?.Voucher);
          }
          if (data?.Campaign.Messages?.[0].CampaignTemplates) {
            setSelectedMessageTemplates(
              data?.Campaign.Messages?.[0].CampaignTemplates,
            );
          }

          if (data?.CampaignPerformance) {
            setCardData((prev) =>
              prev.map((card) => {
                if (card.key === "card-data-1")
                  return {
                    ...card,
                    value: `$${data?.CampaignPerformance?.RevenueRewards ?? 0}`,
                    order: data?.CampaignPerformance?.TotalOrderRewards ?? 0,
                  };
                if (card.key === "card-data-2")
                  return {
                    ...card,
                    value: `$${
                      data?.CampaignPerformance?.RevenueCampaign ?? 0
                    }`,
                    order: data?.CampaignPerformance?.TotalOrderCampaign ?? 0,
                  };
                if (card.key === "card-data-3")
                  return {
                    ...card,
                    value: `${
                      data?.CampaignPerformance?.ROI.toLocaleString("en-US") ??
                      0
                    }%`,
                  };
                if (card.key === "card-data-4")
                  return {
                    ...card,
                    value: `${data?.CampaignPerformance?.CTR ?? 0}%`,
                  };
                return card;
              }),
            );
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingEVoucher(true);
        const dataEVoucher = await campaignService.getAllEVouchers(
          campaignInfo?.Email ?? "",
        );
        if (dataEVoucher) {
          const mapped = mapToEVouchers(dataEVoucher);
          setListEVoucher(mapped);
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingEVoucher(false);
      }
    }
  };

  const fetchCampaignInfo = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getCampaignInfo(brandId);
        if (data) {
          setCampaignInfo(data);
        }
      } catch (error) {
        console.error("Error fetching campaign info:", error);
      }
    }
  };

  useEffect(() => {
    fetchCampaignData();
    fetchCampaignInfo();
  }, [id]);

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
  }, [campaignInfo]);

  useEffect(() => {
    if (data?.SKU && listEVoucher) {
      const findSelectedEVoucher = listEVoucher.find((v) => v.SKU === data.SKU);
      if (findSelectedEVoucher) {
        setSelectedEVoucher(findSelectedEVoucher);
      }
    }
  }, [listEVoucher, data]);

  const handleConfirm = useCallback(async () => {
    if (!variant) return;

    if (variant === "activate") {
      setBillingContext("activate");
      setBillingModalOpen(true);
    }
    if (variant === "delete") {
      const result = await campaignService.deleteCampaignDetails(data?.Id ?? 0);

      if (result === 1) {
        setActionType("delete");
        setIsModalSuccessDeleteCampaignVisible(true);
      }
    }
    if (variant === "cancel") {
      const result = await campaignService.updateCampaignDetailsStatus(
        data?.Id ?? 0,
        StatusEnum.Cancelled,
      );
      if (result == 1) {
        setActionType("cancel");
        setIsModalSuccessDeleteCampaignVisible(true);
        // fetchHistoryCampaign();
      }
    }
    setVariant(null);
  }, [variant]);

  return (
    <Card
      title={
        <Row justify="space-between" align="middle">
          <Col>
            Detail {data?.Channel ? ChannelKeyMap[data?.Channel] : ""} Campaign
          </Col>
          <Col>
            <Button
              color="primary"
              variant="outlined"
              onClick={() =>
                navigateWithQuery(
                  RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                  "id",
                  null,
                )
              }
            >
              Back
            </Button>
          </Col>
        </Row>
      }
    >
      {data?.Status === StatusEnum.Completed && (
        <Card
          title={"Campaign Performance"}
          className="card-campaign-performance"
          style={{ marginBottom: 15 }}
          loading={loading}
        >
          <Row gutter={[12, 12]}>
            {cardData?.map((item, index) => {
              const isFirst = index % 3 === 0;
              const isLast = index % 3 === 2;

              return (
                <Col
                  md={6}
                  xl={6}
                  key={index}
                  style={{ width: isMobile ? "100%" : "unset" }}
                  // style={{
                  //   marginBottom: isMobileOrTablet && !isLast ? 20 : 0,
                  // }}
                >
                  <Card className="card-member">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        height: 70,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: "1.2",
                          }}
                        >
                          {item.title}
                          {item.tooltip && (
                            <Tooltip title={item.tooltip}>
                              <InfoCircleOutlined
                                style={{
                                  fontSize: 14,
                                  marginLeft: 6,
                                  color: "#888",
                                }}
                              />
                            </Tooltip>
                          )}
                        </div>
                        <div style={{ fontSize: 24, fontWeight: 700 }}>
                          {item.value}
                        </div>
                        {item.key === "card-data-1" ||
                        item.key === "card-data-2" ? (
                          <Alert
                            className="alert-show-count"
                            description={
                              <div>
                                <span style={{ color: "#1565C0" }}>
                                  {item.order} Order
                                </span>
                              </div>
                            }
                            type="info"
                          />
                        ) : (
                          <div style={{ height: 25 }}></div>
                        )}
                      </div>
                      <img src={item.icon} alt="icon" width="50" height="50" />
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>
      )}

      <Table<Row>
        className="table-no-header"
        columns={columns}
        dataSource={overviewRows}
        bordered
        size="small"
        title={() => "Campaign Overview"}
        pagination={false}
        rowKey="key"
      />

      <Table<Row>
        className="table-no-header"
        columns={columns}
        dataSource={scheduleRows}
        bordered
        size="small"
        style={{ marginTop: 10 }}
        title={() => "Schedule Time"}
        pagination={false}
        rowKey="key"
      />

      <Table<Row>
        className="table-no-header"
        columns={columns}
        dataSource={messageRows}
        bordered
        size="small"
        style={{ marginTop: 10 }}
        title={() => "Message Content"}
        pagination={false}
        rowKey="key"
      />
      {data?.Status === StatusEnum.Completed && (
        <CampaignMemberLogTable
          data={data?.CampaignRunLog?.[0]?.MemberLogs ?? []}
          campaign={data}
        />
      )}
      {data?.Status === StatusEnum.Draft && (
        <Row
          gutter={[12, 12]}
          justify="space-between"
          style={{ marginTop: 15 }}
        >
          <Col xl={12} md={12} xs={24}>
            <Button
              danger
              onClick={() => setVariant("delete")}
              size="large"
              style={{ width: isMobile ? "100%" : "unset" }}
            >
              Delete Campaign
            </Button>
          </Col>
          {isMobile && (
            <Col xl={6} md={6} xs={24} style={{ textAlign: "right" }}>
              <Button
                color="primary"
                variant="outlined"
                onClick={handleEdit}
                size="large"
                style={{ width: isMobile ? "100%" : "unset" }}
              >
                Edit Campaign
              </Button>
            </Col>
          )}

          {!isMobile ? (
            <Col
              xl={12}
              md={12}
              xs={24}
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexWrap: "wrap",
                textAlign: "right",
              }}
            >
              <Button
                color="primary"
                variant="outlined"
                onClick={handleEdit}
                size="large"
                style={{ width: isMobile ? "100%" : "unset" }}
              >
                Edit Campaign
              </Button>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Activation is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }
              >
                <span>
                  <Button
                    type="primary"
                    onClick={() => {
                      onClickAction("activate");
                    }}
                    size="large"
                    disabled={isWithinOneHour}
                    style={{
                      pointerEvents: isWithinOneHour ? "none" : "auto",
                      width: isMobile ? "100%" : "unset",
                    }}
                  >
                    Activate Campaign
                  </Button>
                </span>
              </Tooltip>
            </Col>
          ) : (
            <Col xl={6} md={6} xs={24} style={{ textAlign: "right" }}>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Activation is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }
              >
                <span>
                  <Button
                    type="primary"
                    onClick={() => {
                      onClickAction("activate");
                    }}
                    size="large"
                    disabled={isWithinOneHour}
                    style={{
                      pointerEvents: isWithinOneHour ? "none" : "auto",
                      width: isMobile ? "100%" : "unset",
                    }}
                  >
                    Activate Campaign
                  </Button>
                </span>
              </Tooltip>
            </Col>
          )}
        </Row>
      )}
      {data?.Status === StatusEnum.Active && (
        <Row
          justify="space-between"
          align="middle"
          style={{ marginTop: 15, flexWrap: "nowrap", gap: 12 }}
        >
          {/* Far left — Cancel */}
          <Col flex="none">
            <Tooltip
              title={
                isWithinOneHour ? (
                  <div className="custom-bubble-tooltip">
                    <strong>Cancellation is unavailable.</strong>
                    <div>
                      This campaign is locked as it gets closer to the scheduled
                      send time.
                    </div>
                  </div>
                ) : (
                  ""
                )
              }
            >
              <span>
                <Button
                  color="danger"
                  onClick={() => onClickAction("cancel")}
                  size="large"
                  disabled={isWithinOneHour || isBilled}
                  style={{
                    pointerEvents:
                      isWithinOneHour || isBilled ? "none" : "auto",
                  }}
                >
                  Cancel Campaign
                </Button>
              </span>
            </Tooltip>
          </Col>

          {/* Far right — Edit + Complete Payment */}
          <Col flex="none">
            <Space>
              <Tooltip
                title={
                  isWithinOneHour ? (
                    <div className="custom-bubble-tooltip">
                      <strong>Editing is unavailable.</strong>
                      <div>
                        This campaign is locked as it gets closer to the
                        scheduled send time.
                      </div>
                    </div>
                  ) : (
                    ""
                  )
                }
              >
                <span>
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleEdit}
                    size="large"
                    disabled={isWithinOneHour || isBilled}
                    style={{
                      pointerEvents:
                        isWithinOneHour || isBilled ? "none" : "auto",
                    }}
                  >
                    Edit Campaign
                  </Button>
                </span>
              </Tooltip>

              {!isBilled && (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    setBillingContext("pay");
                    setBillingModalOpen(true);
                  }}
                >
                  Complete Payment
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      )}
      {data?.Status === StatusEnum.Cancelled && (
        <Row
          gutter={[12, 12]}
          justify="space-between"
          style={{ marginTop: 15 }}
        >
          <Col span={12}></Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Space>
              <Button
                size="large"
                color="danger"
                variant="outlined"
                onClick={() => setVariant("delete")}
              >
                Delete Campaign
              </Button>
            </Space>
          </Col>
        </Row>
      )}
      {variant && (
        <CampaignAlertModal
          open
          variant={variant}
          onCancel={() => setVariant(null)}
          onConfirm={handleConfirm}
        />
      )}
      <Modal
        open={isModalSuccessActivateCampaignVisible}
        onCancel={() => {
          setIsModalSuccessActivateCampaignVisible(false);
        }}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign Active successfully
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your campaign is set to be sent on{" "}
            {dayjs(data?.CampaignStart).format("YYYY-MM-DD HH:mm")}. You can
            view or edit it from the campaign history
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              window.location.reload();
              // navigateWithQuery(RoutePaths.IFRAME_DETAIL_CAMPAIGN, "id", id);
            }}
            style={{ marginTop: 8 }}
          >
            View Details
          </Button>
        </Row>
      </Modal>
      <Modal
        open={isModalSuccessDeleteCampaignVisible}
        onCancel={() => {
          setIsModalSuccessDeleteCampaignVisible(false);
        }}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Campaign {actionType === "delete" ? "Deleted" : "Canceled"}
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            The Campaign has been{" "}
            {actionType === "delete"
              ? "permanently deleted"
              : "successfully canceled"}
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              navigateWithQuery(
                RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                "id",
                null,
              );
              setIsModalSuccessDeleteCampaignVisible(false);
            }}
            style={{ marginTop: 8 }}
          >
            Ok
          </Button>
        </Row>
      </Modal>
      <BillingModal
        open={billingModalOpen}
        campaignId={data?.Id ?? 0}
        campaignRunLogId={
          data?.CampaignRunLog?.reduce(
            (latest, current) => {
              if (!latest) return current;

              const currentTime = current.CreatedDate?.getTime() ?? -Infinity;
              const latestTime = latest.CreatedDate?.getTime() ?? -Infinity;

              return currentTime > latestTime ? current : latest;
            },
            undefined as (typeof data.CampaignRunLog)[number] | undefined,
          )?.Id ?? 0
        }
        campaignName={data?.Name ?? ""}
        channel={data?.Channel ?? null}
        estimatedMembers={data?.EstimatedTotalMembers ?? 0}
        costPerMessage={getChannelCost(data?.Channel ?? null)}
        maxBudget={data?.Budget}
        campaignStart={data?.CampaignStart}
        campaignEnd={data?.CampaignEnd}
        mode={data?.Mode}
        hidePayLater={billingContext === "pay"}
        smsSegments={
          data?.Channel === ChannelEnum.Sms
            ? getSmsSegments(
                data?.Messages?.[0]?.Content ?? "",
                isGsmText(data?.Messages?.[0]?.Content ?? ""),
              )
            : 1
        }
        onClose={() => setBillingModalOpen(false)}
        onConfirmPayment={
          billingContext === "activate"
            ? async (_email, referenceCode) => {
                try {
                  setLoading(true);
                  await campaignService.updateCampaignDetailsStatus(
                    data?.Id ?? 0,
                    StatusEnum.Active,
                    true,
                    referenceCode,
                  );
                } finally {
                  setLoading(false);
                }
              }
            : undefined
        }
        onPaymentLater={async () => {
          setBillingModalOpen(false);
          if (billingContext === "activate") {
            await campaignService.updateCampaignDetailsStatus(
              data?.Id ?? 0,
              StatusEnum.Active,
              false,
            );
            setIsModalSuccessActivateCampaignVisible(true);
          }
        }}
        onSuccess={() => {
          setBillingModalOpen(false);
          setPaymentSuccessModalOpen(true);
        }}
        onFailed={() => {
          setBillingModalOpen(false);
          setPaymentFailedModalOpen(true);
        }}
      />
      <Modal
        open={paymentSuccessModalOpen}
        onCancel={() => setPaymentSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Payment Successful
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your payment has been processed successfully.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row>
          <Button
            type="primary"
            size="large"
            className="w-100"
            onClick={() => {
              setPaymentSuccessModalOpen(false);
              window.location.reload();
            }}
            style={{ marginTop: 8 }}
          >
            View Details
          </Button>
        </Row>
      </Modal>
      <Modal
        open={paymentFailedModalOpen}
        onCancel={() => setPaymentFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={true}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#e74c3c" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Payment Failed
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            We were unable to process your payment. You can retry later.
          </Text>
        </Space>
        <Divider style={{ margin: 5 }} />
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Button
              size="large"
              className="w-100"
              onClick={async () => {
                setPaymentFailedModalOpen(false);
                setBillingModalOpen(false);
                if (billingContext === "activate") {
                  setLoading(true);
                  try {
                    await campaignService.updateCampaignDetailsStatus(
                      data?.Id ?? 0,
                      StatusEnum.Active,
                      false,
                    );
                    setIsModalSuccessActivateCampaignVisible(true);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              style={{ marginTop: 8 }}
            >
              Set Up Payment Later
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              className="w-100"
              onClick={() => {
                setPaymentFailedModalOpen(false);
                setBillingModalOpen(true);
              }}
              style={{ marginTop: 8 }}
            >
              Retry Payment
            </Button>
          </Col>
        </Row>
      </Modal>
      <RFMGeneratingModal
        visible={loading}
        message="Preparing Campaign Data..."
      />
      <CustomModal
        type="timeout"
        title={titleTimeoutModal}
        message={messageTimeoutModal}
        isVisible={showTimeout}
        onClose={() => setShowTimeout(false)}
        autoClose={false}
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={data?.CampaignRunLog?.[0]?.MemberLogs ?? []}
        isDetailPage={true}
      />
    </Card>
  );
};

export default DetailCampaign;
